// Watch mode (battle design 2.4): the lead's move bar in its team slot. One icon per move of its kit with a
// cooldown ring that fills as the cooldown runs, bright when the move is ready. The client predicts the rings from
// MOVES and the hunt's `hit` events (client/moveclock.ts); the server never sends cooldowns.
//
// The rings are driven by the Web Animations API, so the several team-panel patches a second never restart or
// reset them. In battery saver and with reduced motion nothing animates: each render writes the ring's state
// into the markup instead, and it moves in steps.
import { MOVE, WAVE_MIN_TARGETS, WAVE_SPLASH, speciesKit, kitAt, type MoveDef, type KitSlot } from '../shared/moves.ts';
import { ELEMENTS, type Element } from '../shared/elements.ts';
import { moveCooldownMs } from '../shared/combat.ts';
import { SPECIES, LINES } from '../shared/species.ts';
import type { CreatureDTO, GameEvent } from '../shared/protocol.ts';
import { MoveClock } from './moveclock.ts';
import { spriteUrl } from './assets.ts';
import { S } from './state.ts';
import { fighterOf, esc } from './fmt.ts';
import { batterySaver, reducedMotion } from './field.ts';

export const clock = new MoveClock();

/** The element's painted emblem (art/rig3d/icons/icon-el-*.ts); assets.ts watchArtIcons draws it at the size it shows. */
export function glyphImg(el: Element) {
  return `<img class="glyph" src="${spriteUrl(`icon-el-${el}`)}" alt="" aria-hidden="true">`;
}

const BAR_SLOTS: { slot: Exclude<KitSlot, 'basic'>; label: string }[] = [
  { slot: 'light', label: 'Light' }, { slot: 'heavy', label: 'Heavy' }, { slot: 'coverage', label: 'Coverage' }, { slot: 'wave', label: 'Wave' }, { slot: 'signature', label: 'Signature' }, { slot: 'awakened', label: 'Awakened' },
];
const UNLOCK: Record<string, string> = { heavy: 'Learned at Lv 20', coverage: 'Learned at Lv 40', wave: 'Learned at Lv 60', signature: 'Learned on its final stage' };

const quiet = () => batterySaver() || reducedMotion();

/** The lead's move bar: Light, Heavy, Coverage, Wave, Signature, with moves not learned yet shown locked. */
/** foreign: someone else's creature (the read-only creature view), so the viewer's own Hall bonuses don't apply. */
export function moveBarHtml(c: CreatureDTO, foreign = false): string {
  const sp = SPECIES[c.species];
  const line = LINES.find((l) => l.id === sp.line);
  if (!line) return '';
  const full = speciesKit(c.species, c.tablet);
  const has = kitAt(c.species, c.level, c.tablet);
  const f = fighterOf(c, foreign ? undefined : S.trainer?.halls);
  const now = performance.now();
  const still = quiet();
  const icons = BAR_SLOTS.map(({ slot, label }) => {
    const id = full[slot];
    const m = id ? MOVE[id] : undefined;
    if (!m) return '';
    const el = m.element as Element;
    if (!has[slot]) {
      return `<span class="mv locked${slot === 'signature' ? ' sig' : ''}" data-key="${slot}-locked" title="${esc(`${label}: ${m.name}. ${UNLOCK[slot]}.`)}">${ring()}${glyphImg(el)}</span>`;
    }
    const cd = moveCooldownMs(f, m);
    // Quiet modes write the ring's state into the markup; otherwise the animation owns it.
    const p = still ? clock.progress(c.id, m.id, now) : 1;
    const cooling = p < 1 ? `;--p:${Math.floor(p * 20) / 20}` : '';
    return `<span class="mv ${slot === 'signature' ? 'sig' : slot === 'awakened' ? 'sig awk' : slot}${cooling ? ' cd' : ''}" data-key="${m.id}" data-mv="${m.id}" style="--el:${ELEMENTS[el].color}${cooling}" title="${esc(`${label}: ${m.name} · ${ELEMENTS[el].name} · power ${m.power} · ${(cd / 1000).toFixed(1)} s cooldown${m.wave ? ` · on the hunt, also hits wilds next to its target at ${Math.round(WAVE_SPLASH * 100)}% when ${WAVE_MIN_TARGETS} or more are in reach` : ''}${slot === 'coverage' && c.tablet ? ' · Rune Tablet' : ''}`)}">${ring()}${glyphImg(el)}</span>`;
  }).join('');
  return `<div class="movebar" data-key="mb${c.id}" data-cid="${c.id}" role="group" aria-label="Moves">${icons}</div>`;
}

function ring() {
  return '<svg class="ring" viewBox="0 0 36 36" aria-hidden="true"><circle class="trk" cx="18" cy="18" r="15.5"/><circle class="arc" cx="18" cy="18" r="15.5" pathLength="100"/></svg>';
}

/** Which cooldown each icon's running animation shows, so a patch or a re-sync never restarts it. */
const shown = new WeakMap<HTMLElement, number>();

/** Starts (or lines up) the ring animations of the move bar on screen. Cheap: call it after every render. */
export function syncMoveBar(castNow?: string) {
  const bar = document.querySelector('#teamlist .movebar') as HTMLElement | null;
  if (!bar) return;
  const cid = Number(bar.dataset.cid);
  const still = quiet();
  const now = performance.now();
  for (const el of bar.querySelectorAll<HTMLElement>('.mv[data-mv]')) {
    const mv = el.dataset.mv!;
    const arc = el.querySelector('.arc') as SVGElement | null;
    const glyph = el.querySelector('.glyph') as HTMLElement | null;
    if (!arc || !glyph) continue;
    if (still) {
      if (shown.has(el)) { for (const a of [...arc.getAnimations(), ...glyph.getAnimations(), ...el.getAnimations()]) a.cancel(); shown.delete(el); }
      continue;
    }
    const cd = clock.get(cid, mv, now);
    if (!cd) continue;
    if (shown.get(el) === cd.start) continue;
    shown.set(el, cd.start);
    for (const a of [...arc.getAnimations(), ...glyph.getAnimations()]) a.cancel();
    const duration = cd.end - cd.start;
    const run = arc.animate([{ strokeDashoffset: 100, opacity: 0.7 }, { strokeDashoffset: 0, opacity: 0.7 }], { duration });
    run.currentTime = now - cd.start;
    const dim = glyph.animate([{ opacity: 0.38, filter: 'saturate(.3)' }, { opacity: 0.38, filter: 'saturate(.3)', offset: 0.995 }, { opacity: 1, filter: 'none' }], { duration });
    dim.currentTime = now - cd.start;
    // Ready again: a short glint on the icon.
    run.onfinish = () => { if (el.isConnected && !quiet()) el.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.18)', offset: 0.4 }, { transform: 'scale(1)' }], { duration: 260, easing: 'ease-out' }); };
    if (castNow === mv) el.animate([{ filter: 'brightness(2.2)', transform: 'scale(.86)' }, { filter: 'none', transform: 'scale(1)' }], { duration: 300, easing: 'ease-out' });
  }
}

/** Hunt events: a lead blow with a move starts that move's cooldown for the creature leading now. */
export function watchEvents(evs: GameEvent[]) {
  const id = S.field?.lead?.id;
  if (id === undefined) return;
  let cast = '';
  for (const e of evs) {
    if (e.k !== 'hit' || e.by !== 'lead' || e.splash) continue;
    const m: MoveDef | undefined = MOVE[e.mv];
    const c = S.creatures.get(id);
    if (!m || m.slot === 'basic' || !c) continue;
    clock.cast(id, m.id, performance.now(), moveCooldownMs(fighterOf(c, S.trainer?.halls), m));
    cast = m.id;
  }
  if (cast && !document.hidden) syncMoveBar(cast);
}
